import {
  Box,
  CircularProgress,
} from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import BaseHighchart from '../../BaseHighchart';
import {
  ChartDataType,
  ChartFiltersWiseconn,
} from '../../types';
import { calculateSeries, prepareData } from '../builder';

import { RADIATION_NAME, CHART_TITLE_RADIACION } from '../../../../constants/graphs';
import apiClient from '../../../../requests/api/apiClient';
import { ConversationSchema, TimePointSchema } from '../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../types';
import { navigateToChart } from '../../../../utils/helpers';
import { FetchError } from '../../../../views/types';
import ChartFilter from '../../ChartFilter';
import ConversationModal from '../../ConversationTab/conversationModal';
import { FormattedSeries } from '../types';
import {
  calculateYAxisLimits,
  getChartConfig,
  getStackedTooltip,
  getYAxis,
} from '../utils';
import ChartConfigDialog from '../../ChartConfigDialog/ChartConfigDialog';
import RadiationConfigContent from './RadiationConfigContent';
import useChartConfigClick from '../../../../hooks/useChartConfigClick';

function RadiationChart({
  chartView,
  sectorId,
  farmId,
  hideLegend = false,
  hideXValues = false,
  hideYValues = false,
  height = 600,
  title = CHART_TITLE_RADIACION,
  yAxisTitle = 'Radiación solar (W/m²)',
  externalModalState,
  externalSetModalState,
  externalConversations,
  externalSetConversations,
  onConversationCreated,
  isWeatherStation,
}: {
  chartView: 'dashboard' | 'graphs' | 'settings' | 'fullScreen'
  sectorId: string
  farmId: string
  hideLegend?: boolean
  hideXValues?: boolean
  hideYValues?: boolean
  height?: number | string
  title?: string
  yAxisTitle?: string
  externalModalState?: {
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  };
  externalSetModalState?: React.Dispatch<React.SetStateAction<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>>;
  externalConversations?: ConversationSchema[];
  externalSetConversations?: React.Dispatch<React.SetStateAction<ConversationSchema[]>>;
  onConversationCreated?: () => void;
  isGeneratingPDF?: boolean;
  isWeatherStation?: boolean;
}) {
  const chartName = 'radiacion-solar';
  const [chartData, setChartData] = useState<ChartDataType>({
    series: [],
    categories: [],
    absoluteCategories: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [internalModalState, setInternalModalState] = useState<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>({
    open: false,
    conversationId: '',
    timePointData: {
      hour: '',
      day: '',
      month: '',
      year: '',
    },
  });
  const [internalConversations, setInternalConversations] = useState<ConversationSchema[]>([]);

  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);
  const selectedSector = useSelector((state: ReduxState) => state.currentSector.selectedSector);

  const modalState = externalModalState || internalModalState;
  const setModalState = externalSetModalState || setInternalModalState;
  const conversations = externalConversations || internalConversations;
  const setConversations = externalSetConversations || setInternalConversations;

  const transformData = useCallback(
    (
      preparedData: FormattedSeries[],
    ) => {
      if (!interval) return;
      const seriesData = calculateSeries(
        preparedData,
        true,
        interval.startDate,
        interval.endDate,
      );

      setChartData({
        ...seriesData,
        series: seriesData.series.map((series) => ({
          ...series,
          name: series.name.replace('Solar Radiation', 'Radiación solar'),
        })),
      });
    }, [interval],
  );

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      if (interval) {
        const chartFilters: ChartFiltersWiseconn = {
          farmId,
          startDate: interval.startDate,
          endDate: interval.endDate,
          sensorType: 'Solar Radiation',
          sectorId,
          sn: selectedSector?.telemetryType === 'sn' ? selectedSector.weatherZoneId : undefined,
          chart: RADIATION_NAME,
          isWeatherStation,
        };
        const preparedData = await prepareData({
          ...chartFilters,
        }, selectedSector?.telemetryType);

        transformData(preparedData);
      }
    } finally {
      setIsLoading(false);
    }
  }, [farmId,
    interval,
    transformData,
    selectedSector,
    sectorId,
    isWeatherStation,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const customTooltip = useMemo(() => ({
    formatter(this: Highcharts.TooltipFormatterContextObject) {
      return getStackedTooltip(this, conversations);
    },
  }), [conversations]);

  const chartConfig = useMemo(() => {
    if (!interval) return null;

    const yLimits = calculateYAxisLimits(chartData.series as any[]);

    return getChartConfig(
      chartData,
      height,
      hideXValues,
      {
        ...(getYAxis(yAxisTitle, hideYValues) as Highcharts.YAxisOptions),
        min: yLimits.min,
        max: yLimits.max,
      },
      dayjs(interval.startDate).valueOf(),
      dayjs(interval.endDate).valueOf(),
    );
  }, [
    hideXValues,
    chartData.absoluteCategories,
    hideYValues,
    yAxisTitle,
    height,
    interval,
  ]);

  useEffect(() => {
    if (!externalConversations) {
      const loadConversations = async () => {
        try {
          const { data } = await apiClient.conversation.conversationDetail(
            sectorId,
            RADIATION_NAME,
          );
          setInternalConversations(data.conversations);
        } catch (err) {
          const { error } = err as FetchError;
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      };

      loadConversations();
    }
  }, [sectorId, externalConversations]);

  const handleConfigClick = useChartConfigClick({
    chartView,
    route: chartName,
    farmId,
    sectorId,
    onFullScreen: () => setConfigDialogOpen(true),
  });

  const handleFullScreenClick = useCallback(() => {
    navigateToChart(`fullScreen/${chartName}`, history, {
      farmId,
      sectorId,
    });
  }, [history, farmId, sectorId]);

  return (
    <>
      <Box>
        {isLoading && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress />
          </Box>
        )}
        <div style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
          <BaseHighchart
            chartView={chartView}
            sectorId={sectorId}
            graphName={RADIATION_NAME}
            filter={chartView === 'settings' ? <ChartFilter sectorId={sectorId} /> : undefined}
            title={title}
            yAxisTitle={yAxisTitle}
            series={chartData.series as Highcharts.SeriesOptionsType[]}
            tooltip={customTooltip}
            hideYValues={hideYValues}
            showFullScreen={true}
            legend={{ enabled: !hideLegend }}
            chartConfig={chartConfig}
            onConfigClick={handleConfigClick}
            onFullScreenClick={handleFullScreenClick}
            absoluteCategories={chartData.absoluteCategories}
            setModalState={setModalState}
            conversations={conversations}
            height={height}
            zoomSync={true}
            autoAdjustYAxis={true}
            interval={interval}
          />
        </div>
      </Box>
      <ConversationModal
        modalState={modalState}
        setModalState={setModalState}
        farmId={farmId}
        sectorId={sectorId}
        chart={RADIATION_NAME}
        setConversationsFunction={setConversations}
        onConversationCreated={onConversationCreated}
      />
      <ChartConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        title="Configuración · Radiación solar"
      >
        <RadiationConfigContent sectorId={sectorId} />
      </ChartConfigDialog>
    </>
  );
}

export default RadiationChart;
